import React, { useEffect, useState } from "react";
import { CalendarRange, ChevronLeft, ChevronRight, Loader2, RefreshCw, Sparkles, User } from "lucide-react";
import { Link } from "react-router-dom";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { api } from "../api";
import { useApp } from "../context/AppContext";

const fmtD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const mondayOf = (d) => { const x = new Date(d); x.setHours(12, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const shortD = (iso, lang) => new Date(`${iso}T12:00:00`).toLocaleDateString(lang === "en" ? "en-US" : lang === "es" ? "es-ES" : "pt-BR", { day: "2-digit", month: "short" });

/** AI summary of the user's own jobs for one week (Mon..Sun). */
export const WeeklySummaryCard = () => {
  const { user, t, lang } = useApp();
  const [open, setOpen] = useState(false);
  const [weekStart, setWeekStart] = useState(() => fmtD(mondayOf(new Date())));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const thisMonday = fmtD(mondayOf(new Date()));

  const load = async (force = false) => {
    setBusy(true);
    try {
      const r = await api.get("/weekly-summary", { params: { week_start: weekStart, user_id: user.id, force: force ? 1 : 0 }, timeout: 60000 });
      setData(r.data);
    } catch (e) { setData({ error: true }); } finally { setBusy(false); }
  };
  useEffect(() => { if (open) { setData(null); load(false); } /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, weekStart]);

  const shift = (n) => {
    const d = new Date(`${weekStart}T12:00:00`); d.setDate(d.getDate() + 7 * n);
    const next = fmtD(d);
    if (next > thisMonday) return;
    setWeekStart(next);
  };
  const weekEnd = (() => { const d = new Date(`${weekStart}T12:00:00`); d.setDate(d.getDate() + 6); return fmtD(d); })();

  return (
    <Card className="p-3 mb-5 border-primary/20" data-testid="weekly-summary-card">
      <button type="button" className="w-full flex items-center gap-2 text-left" onClick={() => setOpen((o) => !o)} data-testid="weekly-summary-toggle">
        <span className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><CalendarRange className="h-4 w-4" /></span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold">{t("weekly_summary")}</span>
          <span className="block text-[11px] text-muted-foreground truncate">{t("weekly_summary_hint")}</span>
        </span>
        <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-3" data-testid="weekly-summary-body">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="weekly-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-xs font-medium flex-1 text-center" data-testid="weekly-range">{shortD(weekStart, lang)} – {shortD(weekEnd, lang)}</span>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(1)} disabled={weekStart >= thisMonday} data-testid="weekly-next"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => load(true)} disabled={busy} data-testid="weekly-refresh">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
          {busy && !data && <p className="text-xs text-muted-foreground flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-primary" /> {t("summary_generating")}</p>}
          {data?.error && <p className="text-xs text-destructive">{t("something_wrong")}</p>}
          {data && !data.error && data.empty && <p className="text-xs text-muted-foreground" data-testid="weekly-empty">{t("weekly_summary_empty")}</p>}
          {data && !data.error && !data.empty && (
            <div className="space-y-4" data-testid="weekly-text">
              {/* totals */}
              <div className="grid grid-cols-3 gap-2" data-testid="weekly-totals">
                {[[data.jobs, t("weekly_jobs")], [data.units, t("weekly_units")], [(data.crews || []).length, t("weekly_crews")]].map(([n, l]) => (
                  <div key={l} className="rounded-xl bg-muted/60 px-3 py-2 text-center">
                    <div className="text-xl font-bold leading-none">{n}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">{l}</div>
                  </div>
                ))}
              </div>
              {data.overview && (
                <p className="text-sm leading-relaxed flex gap-2" data-testid="weekly-overview">
                  <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span>{data.overview}</span>
                </p>
              )}
              {data.highlights?.length > 0 && (
                <ul className="text-xs space-y-1 rounded-xl border border-amber-300/40 bg-amber-50/60 dark:bg-amber-500/10 p-3" data-testid="weekly-highlights">
                  {data.highlights.map((h, i) => <li key={i} className="flex gap-2"><span className="text-amber-600">•</span><span>{h}</span></li>)}
                </ul>
              )}
              {/* per crew, like the daily page */}
              {(data.crews || []).map((g) => (
                <section key={g.code} className="space-y-2" data-testid={`weekly-crew-${g.code}`}>
                  <div className="flex items-center gap-2 pt-1">
                    <span className="rounded-md bg-primary text-primary-foreground text-[11px] font-bold px-2 py-0.5">{g.code}</span>
                    {g.supervisor_name && <span className="text-xs text-muted-foreground truncate flex items-center gap-1"><User className="h-3 w-3" />{g.supervisor_name}</span>}
                    <span className="ml-auto text-[11px] text-muted-foreground whitespace-nowrap">{g.jobs.length} {t("weekly_jobs").toLowerCase()} · {g.units} {t("weekly_units").toLowerCase()}</span>
                  </div>
                  {g.overview && <p className="text-xs text-muted-foreground leading-relaxed">{g.overview}</p>}
                  {g.jobs.map((j) => (
                    <Link to={`/jobs/${j.job_id}`} key={j.job_id} className="block rounded-xl border bg-card p-3 hover:border-primary/40 transition-colors" data-testid={`weekly-job-${j.job_number}`}>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-semibold text-primary">#{j.job_number}</span>
                        <span className="font-semibold truncate">{j.customer_name}</span>
                        <span className="ml-auto text-muted-foreground whitespace-nowrap">{shortD(j.date, lang)}{j.time ? ` · ${j.time}` : ""}</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 flex gap-2 flex-wrap">
                        {j.city && <span>{j.city}</span>}
                        {j.units ? <span>· {j.units} {t("weekly_units").toLowerCase()}</span> : null}
                        {j.messages ? <span>· {j.messages} msg</span> : null}
                      </div>
                      {j.summary && <p className="text-sm mt-2 leading-relaxed">{j.summary}</p>}
                      {j.highlights?.length > 0 && (
                        <ul className="mt-1.5 text-xs space-y-0.5">
                          {j.highlights.map((h, i) => <li key={i} className="flex gap-1.5"><span className="text-primary">•</span><span>{h}</span></li>)}
                        </ul>
                      )}
                    </Link>
                  ))}
                </section>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
